import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import {
  ClassFeeStructure,
  FeeLineRequest,
  PaymentMethod,
  SalaryRecord,
  StudentFeeAccount
} from '../models/finance.model';
import { FinanceService } from '../services/finance.service';
import { LocalStorageService } from '../services/local-storage.service';
import { StudentService } from '../services/student.service';
import { TenantService } from '../services/tenant.service';
import { CLASS_GROUP_OPTIONS } from '../school-constants';
import { Student } from '../student';

@Component({
  selector: 'app-accounts',
  templateUrl: './accounts.component.html',
  styleUrls: ['./accounts.component.css']
})
export class AccountsComponent implements OnInit {

  readonly classOptions = [...CLASS_GROUP_OPTIONS];
  readonly paymentMethods: PaymentMethod[] = ['CASH', 'BANK', 'UPI', 'CARD', 'OTHER'];

  isAdmin = false;
  isStudent = false;
  /** Fee collection UI: admin-only (entire Accounts area is admin-only). */
  canCollectFees = false;

  schoolDisplayName = '';

  academicYear = '2025-2026';
  structures: ClassFeeStructure[] = [];
  ledgers: StudentFeeAccount[] = [];
  students: Student[] = [];
  salaries: SalaryRecord[] = [];

  loading = false;

  /** Fee structure editor */
  structClass = '10';
  structLines: FeeLineRequest[] = [];

  /** Collect fees */
  selectedStudentId: number | null = null;
  paymentForm: FormGroup;

  ledgerColumns = ['name', 'className', 'annualTotal', 'totalPaid', 'due', 'actions'];
  ledgerData = new MatTableDataSource<StudentFeeAccount>([]);

  salaryColumns = ['staffName', 'role', 'monthlyAmount', 'paidForMonth', 'actions'];
  salaryData = new MatTableDataSource<SalaryRecord>([]);

  salaryForm: FormGroup;
  markPaidMonth = '';

  constructor(
    private fb: FormBuilder,
    private financeService: FinanceService,
    private studentService: StudentService,
    private localStorage: LocalStorageService,
    private tenantService: TenantService,
    private snackBar: MatSnackBar
  ) {
    this.paymentForm = this.fb.group({
      amount: ['', [Validators.required, Validators.min(1)]],
      method: ['CASH' as PaymentMethod, Validators.required],
      remark: ['']
    });
    this.salaryForm = this.fb.group({
      staffName: ['', Validators.required],
      role: ['', Validators.required],
      monthlyAmount: ['', [Validators.required, Validators.min(0)]],
      notes: ['']
    });
  }

  ngOnInit(): void {
    this.isAdmin = this.localStorage.isAdmin();
    this.isStudent = this.localStorage.isStudent();
    this.canCollectFees = this.localStorage.isAdmin();
    this.tenantService.getDisplayName().subscribe(name => {
      this.schoolDisplayName = (name && name.trim()) ? name.trim() : (this.localStorage.getSchoolDisplayName() || '');
    });
    this.refreshAll();
  }

  refreshAll(): void {
    this.loading = true;
    this.studentService.getAll().subscribe(
      studs => {
        this.students = studs || [];
        this.financeService.getFeeStructures().subscribe(str => {
          this.structures = str || [];
          this.financeService.getLedgers().subscribe(led => {
            this.ledgers = this.financeService.mergeLedgersWithStudents(this.students, led || []);
            this.ledgerData.data = this.ledgers;
            this.financeService.getSalaries().subscribe(sal => {
              this.salaries = sal || [];
              this.salaryData.data = this.salaries;
              this.loading = false;
              this.loadStructLinesForSelection();
            });
          });
        });
      },
      () => {
        this.students = [];
        this.loading = false;
        this.snackBar.open('Could not load students for fee ledgers.', 'Dismiss', { duration: 4000 });
      }
    );
  }

  onAcademicYearChange(): void {
    this.refreshLedgersOnly();
    this.loadStructLinesForSelection();
  }

  private refreshLedgersOnly(): void {
    this.financeService.getLedgers().subscribe(led => {
      this.ledgers = this.financeService.mergeLedgersWithStudents(this.students, led || []);
      this.ledgerData.data = this.ledgers;
    });
  }

  loadStructLinesForSelection(): void {
    const found = this.structures.find(
      s => s.className === this.structClass && s.academicYear === this.academicYear
    );
    this.structLines = found && found.lines?.length
      ? found.lines.map(l => ({ ...l, notes: l.notes ?? '' }))
      : [{ code: 'TUITION', label: 'Tuition fee', amount: 0, notes: '' }];
  }

  onStructClassChange(): void {
    this.loadStructLinesForSelection();
  }

  addFeeLine(): void {
    this.structLines.push({ code: 'FEE', label: 'Fee', amount: 0, notes: '' });
  }

  removeFeeLine(i: number): void {
    this.structLines.splice(i, 1);
    if (!this.structLines.length) {
      this.structLines.push({ code: 'TUITION', label: 'Tuition fee', amount: 0, notes: '' });
    }
  }

  saveFeeStructure(): void {
    if (!this.isAdmin) {
      return;
    }
    const lines = this.structLines
      .map(l => ({
        code: (l.code || '').trim() || 'FEE',
        label: (l.label || '').trim() || 'Fee',
        amount: Number(l.amount) || 0,
        notes: (l.notes || '').trim()
      }))
      .filter(l => l.amount >= 0);
    const entry: ClassFeeStructure = {
      className: this.structClass,
      academicYear: this.academicYear,
      lines
    };
    const rest = this.structures.filter(
      s => !(s.className === entry.className && s.academicYear === entry.academicYear)
    );
    const next = [...rest, entry];
    this.financeService.saveFeeStructures(next).subscribe(
      () => {
        this.structures = next;
        this.refreshLedgersOnly();
        this.snackBar.open('Fee structure saved', 'Dismiss', { duration: 3000 });
      },
      () => {
        this.structures = next;
        this.refreshLedgersOnly();
        this.snackBar.open('Saved locally (server unavailable).', 'Dismiss', { duration: 4000 });
      }
    );
  }

  selectedLedger(): StudentFeeAccount | null {
    if (this.selectedStudentId == null) {
      return null;
    }
    return this.ledgers.find(l => l.studentId === this.selectedStudentId) || null;
  }

  due(acc: StudentFeeAccount): number {
    if (acc.balanceDue != null && !Number.isNaN(Number(acc.balanceDue))) {
      return Number(acc.balanceDue);
    }
    return Math.max(0, (acc.annualTotal || 0) - (acc.totalPaid || 0));
  }

  recordPayment(): void {
    if (!this.canCollectFees || this.paymentForm.invalid || this.selectedStudentId == null) {
      return;
    }
    const acc = this.ledgers.find(l => l.studentId === this.selectedStudentId);
    if (!acc) {
      return;
    }
    const v = this.paymentForm.value;
    const amt = Number(v.amount);
    if (amt <= 0) {
      return;
    }
    const dto = {
      studentId: acc.studentId,
      amount: amt,
      method: v.method as PaymentMethod,
      remark: (v.remark || '').trim() || undefined,
      paymentDate: new Date().toISOString().slice(0, 10)
    };

    this.financeService.postPayment(dto).subscribe(
      () => {
        this.financeService.getLedgers().subscribe(led => {
          this.ledgers = this.financeService.mergeLedgersWithStudents(this.students, led || []);
          this.ledgerData.data = this.ledgers;
          this.paymentForm.reset({ amount: '', method: 'CASH', remark: '' });
          this.snackBar.open('Payment recorded', 'Dismiss', { duration: 3000 });
        });
      },
      () => {
        this.snackBar.open('Payment could not be saved. Try again.', 'Dismiss', { duration: 4000 });
      }
    );
  }

  selectStudentForPayment(id: number): void {
    this.selectedStudentId = id;
    this.paymentForm.reset({ amount: '', method: 'CASH', remark: '' });
  }

  addSalary(): void {
    if (!this.isAdmin || this.salaryForm.invalid) {
      return;
    }
    const v = this.salaryForm.value;
    const row: SalaryRecord = {
      id: 'sal-' + Date.now(),
      staffName: v.staffName.trim(),
      role: v.role.trim(),
      monthlyAmount: Number(v.monthlyAmount),
      notes: (v.notes || '').trim() || undefined
    };
    const next = [...this.salaries, row];
    this.financeService.saveSalaries(next).subscribe(
      () => {
        this.salaries = next;
        this.salaryData.data = next;
        this.salaryForm.reset({ staffName: '', role: '', monthlyAmount: '', notes: '' });
        this.snackBar.open('Salary row added', 'Dismiss', { duration: 3000 });
      },
      () => {
        this.salaries = next;
        this.salaryData.data = next;
      }
    );
  }

  deleteSalary(row: SalaryRecord): void {
    if (!this.isAdmin) {
      return;
    }
    const next = this.salaries.filter(s => s.id !== row.id);
    this.financeService.saveSalaries(next).subscribe(
      () => {
        this.salaries = next;
        this.salaryData.data = next;
      },
      () => {
        this.salaries = next;
        this.salaryData.data = next;
      }
    );
  }

  markSalaryPaid(row: SalaryRecord): void {
    if (!this.isAdmin || !this.markPaidMonth) {
      return;
    }
    row.paidForMonth = this.markPaidMonth;
    this.financeService.saveSalaries([...this.salaries]).subscribe(
      () => {
        this.salaryData.data = [...this.salaries];
        this.snackBar.open('Marked paid for ' + this.markPaidMonth, 'Dismiss', { duration: 3000 });
      },
      () => {
        this.salaryData.data = [...this.salaries];
      }
    );
  }

  totalExpected(): number {
    return this.ledgers.reduce((a, l) => a + (l.annualTotalFee || 0), 0);
  }

  totalCollected(): number {
    return this.ledgers.reduce((a, l) => a + (l.totalPaid || 0), 0);
  }

  totalSalaryMonthly(): number {
    return this.salaries.reduce((a, s) => a + (s.monthlyAmount || 0), 0);
  }
}
